import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, LogOut, Pencil, Plus, Trash2, Package, Receipt, Sparkles, Upload, Check, X, Rocket } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPTY_FORM = { name: "", category: "classic-vintage", price: "", description: "", image: "", tag: "", stock: 50, sizes: { S: 50, M: 50, L: 50 }, drop_ends_at: "" };

const INPUT_CLS = "h-12 w-full border border-navy/20 bg-white/5 px-4 text-navy focus:border-gold focus:outline-none";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("products");
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadProducts = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  const loadOrders = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) {
      loadProducts();
      loadOrders();
    }
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Welcome back to the atelier");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const openForm = (p) => {
    setEditing(p || null);
    setForm(p ? { name: p.name, category: p.category, price: String(p.price), description: p.description, image: p.image, tag: p.tag || "", stock: p.stock, sizes: p.sizes || { S: 50, M: 50, L: 50 }, drop_ends_at: p.drop_ends_at ? p.drop_ends_at.slice(0, 16) : "" } : EMPTY_FORM);
    setDialog(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      stock: Object.values(form.sizes).reduce((a, b) => a + (parseInt(b, 10) || 0), 0),
      sizes: { S: parseInt(form.sizes.S, 10) || 0, M: parseInt(form.sizes.M, 10) || 0, L: parseInt(form.sizes.L, 10) || 0 },
      drop_ends_at: form.drop_ends_at && form.category === "limited-edition" ? new Date(form.drop_ends_at).toISOString() : "",
      tag: form.tag,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Piece updated" : "New piece added to the collection");
      setDialog(false);
      loadProducts();
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success(`${p.name} retired from the collection`);
      loadProducts();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  const setOrderStatus = async (o, status, extra = {}) => {
    try {
      await api.patch(`/admin/orders/${o.id}/status`, { status, ...extra });
      toast.success(status === "shipped" ? "Marked as shipped" : "Marked as delivered", {
        description: "The customer has been emailed the news.",
      });
      setShipForm(null);
      loadOrders();
    } catch (err) {
      toast.error("Status update failed", { description: formatApiError(err) });
    }
  };

  const [shipForm, setShipForm] = useState(null);
  const [golive, setGolive] = useState(null);
  const loadGoLive = () => api.get("/admin/go-live").then(({ data }) => setGolive(data)).catch((e) => toast.error("Checklist failed", { description: formatApiError(e) }));

  const [genLoading, setGenLoading] = useState(false);
  const generateImage = async () => {
    if (!form.name.trim()) {
      toast.error("Name the piece first", { description: "The AI art-directs from the product name." });
      return;
    }
    setGenLoading(true);
    try {
      const { data } = await api.post("/admin/generate-image", { name: form.name, category: form.category, hint: form.description });
      setForm((f) => ({ ...f, image: data.image }));
      toast.success("Fresh photography generated", { description: "The new shot is framed and ready." });
    } catch (err) {
      toast.error("Generation failed", { description: formatApiError(err) });
    } finally {
      setGenLoading(false);
    }
  };

  const [uploadLoading, setUploadLoading] = useState(false);
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.image }));
      toast.success("Photography uploaded", { description: "Framed and ready for the collection." });
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err) });
    } finally {
      setUploadLoading(false);
      e.target.value = "";
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B101E]" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" strokeWidth={1.4} />
      </div>
    );
  }

  if (user && user.role !== "admin") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#0B101E] px-6 text-center" data-testid="admin-denied">
        <p className="max-w-md font-serif text-3xl italic text-navy">This door opens for the atelier only.</p>
        <button onClick={logout} data-testid="admin-denied-logout" className="border border-navy/20 px-8 py-3 text-[0.65rem] font-bold uppercase tracking-[0.24em] text-navy transition-colors duration-300 hover:border-gold hover:text-gold">
          Sign Out
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B101E] px-6" data-testid="admin-login-page">
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm border border-gold/25 bg-navy-soft p-10"
          data-testid="admin-login-form"
        >
          <div className="mb-10 text-white"><Logo tone="navy" /></div>
          <p className="mb-8 text-[0.65rem] font-bold uppercase tracking-[0.3em] text-gold">Atelier Access</p>
          <label className="mb-5 block">
            <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-white/60">Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input"
              className="h-12 w-full border border-white/20 bg-transparent px-4 text-white focus:border-gold focus:outline-none" />
          </label>
          <label className="mb-7 block">
            <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-white/60">Password</span>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input"
              className="h-12 w-full border border-white/20 bg-transparent px-4 text-white focus:border-gold focus:outline-none" />
          </label>
          {error && <p className="mb-5 text-sm text-red-300" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-submit" className="w-full bg-gold py-4 text-[0.7rem] font-bold uppercase tracking-[0.28em] text-[#0B101E] transition-colors duration-300 hover:bg-[#F2ECDF]">
            Enter the Atelier
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-28 pb-20" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.3em] text-burgundy">Locka Atelier</p>
            <h1 className="mt-2 font-serif text-4xl text-navy">Bonjour, {user.name}</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 border border-navy/20 px-6 py-3 text-[0.65rem] font-bold uppercase tracking-[0.24em] text-navy transition-colors duration-300 hover:border-burgundy hover:text-burgundy">
            <LogOut className="h-4 w-4" strokeWidth={1.8} /> Sign Out
          </button>
        </div>

        <div className="mt-10 flex gap-2.5" data-testid="admin-tabs">
          <button onClick={() => setTab("products")} data-testid="admin-tab-products" className={`flex items-center gap-2 border px-6 py-3 text-[0.65rem] font-bold uppercase tracking-[0.24em] transition-colors duration-300 ${tab === "products" ? "border-navy bg-navy text-cream" : "border-navy/20 text-navy/60"}`}>
            <Package className="h-4 w-4" strokeWidth={1.8} /> Collection
          </button>
          <button onClick={() => setTab("orders")} data-testid="admin-tab-orders" className={`flex items-center gap-2 border px-6 py-3 text-[0.65rem] font-bold uppercase tracking-[0.24em] transition-colors duration-300 ${tab === "orders" ? "border-navy bg-navy text-cream" : "border-navy/20 text-navy/60"}`}>
            <Receipt className="h-4 w-4" strokeWidth={1.8} /> Orders
          </button>
          <button onClick={() => { setTab("golive"); loadGoLive(); }} data-testid="admin-tab-golive" className={`flex items-center gap-2 border px-6 py-3 text-[0.65rem] font-bold uppercase tracking-[0.24em] transition-colors duration-300 ${tab === "golive" ? "border-navy bg-navy text-cream" : "border-navy/20 text-navy/60"}`}>
            <Rocket className="h-4 w-4" strokeWidth={1.8} /> Go Live
          </button>
        </div>

        {tab === "products" ? (
          <div className="mt-8" data-testid="admin-products-panel">
            <div className="mb-6 flex justify-end">
              <button onClick={() => openForm(null)} data-testid="admin-add-product-button" className="flex items-center gap-2 bg-navy px-6 py-3.5 text-[0.65rem] font-bold uppercase tracking-[0.24em] text-cream transition-colors duration-300 hover:bg-burgundy hover:text-white">
                <Plus className="h-4 w-4" strokeWidth={2} /> New Piece
              </button>
            </div>
            <div className="overflow-x-auto border border-navy/10 bg-white">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-[0.6rem] font-bold uppercase tracking-[0.24em] text-[#0B101E]/50">
                    <th className="px-5 py-4">Piece</th>
                    <th className="px-5 py-4">Category</th>
                    <th className="px-5 py-4">Price</th>
                    <th className="px-5 py-4">Stock</th>
                    <th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-black/5 last:border-0" data-testid={`admin-product-row-${p.slug}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-4">
                          <img src={p.image} alt={p.name} className="h-12 w-10 object-cover" />
                          <span className="font-serif text-base text-[#0B101E]">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-[#0B101E]/60">{p.category_name}</td>
                      <td className="px-5 py-4 font-serif text-burgundy">${p.price.toFixed(2)}</td>
                      <td className="px-5 py-4 text-[#0B101E]/60">{p.stock}</td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openForm(p)} aria-label={`Edit ${p.name}`} data-testid={`admin-edit-${p.slug}`} className="flex h-9 w-9 items-center justify-center border border-black/15 text-[#0B101E] transition-colors duration-200 hover:bg-[#0B101E] hover:text-white">
                            <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
                          </button>
                          <button onClick={() => remove(p)} aria-label={`Delete ${p.name}`} data-testid={`admin-delete-${p.slug}`} className="flex h-9 w-9 items-center justify-center border border-black/15 text-[#0B101E] transition-colors duration-200 hover:border-burgundy hover:bg-burgundy hover:text-white">
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === "orders" ? (
          <div className="mt-8 border border-navy/10 bg-white" data-testid="admin-orders-panel">
            {orders.length === 0 ? (
              <p className="p-10 text-center font-serif text-xl italic text-[#0B101E]/50" data-testid="admin-orders-empty">No orders yet — the runway awaits.</p>
            ) : (
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-[0.6rem] font-bold uppercase tracking-[0.24em] text-[#0B101E]/50">
                    <th className="px-5 py-4">Order</th>
                    <th className="px-5 py-4">Customer</th>
                    <th className="px-5 py-4">Pieces</th>
                    <th className="px-5 py-4">Total</th>
                    <th className="px-5 py-4">Payment</th>
                    <th className="px-5 py-4">Fulfilment</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-black/5 last:border-0" data-testid={`admin-order-${o.id.slice(0, 8)}`}>
                      <td className="px-5 py-4 font-mono text-xs text-[#0B101E]/60">#{o.id.slice(0, 8)}<br /><span className="text-[#0B101E]/40">{new Date(o.created_at).toLocaleDateString()}</span></td>
                      <td className="px-5 py-4 text-xs text-[#0B101E]/60">{o.customer || "Guest"}</td>
                      <td className="px-5 py-4 text-[#0B101E]/70">
                        {o.items.map((i) => `${i.name}${i.size ? ` (${i.size})` : ""} ×${i.quantity}`).join(", ")}
                        {o.gift_message && (
                          <p className="mt-1.5 font-serif text-xs italic text-[#8a6d1a]" data-testid={`gift-note-${o.id.slice(0, 8)}`}>Gift note: “{o.gift_message}”</p>
                        )}
                      </td>
                      <td className="px-5 py-4 font-serif text-burgundy">${o.amount.toFixed(2)}</td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.18em] ${o.payment_status === "paid" ? "bg-[#0B101E] text-gold" : "bg-black/8 text-[#0B101E]/60"}`}>
                          {o.payment_status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.18em] ${
                            o.fulfillment === "delivered" ? "bg-gold/20 text-[#8a6d1a]" : o.fulfillment === "shipped" ? "bg-burgundy/10 text-burgundy" : "bg-black/6 text-[#0B101E]/50"
                          }`} data-testid={`fulfillment-${o.id.slice(0, 8)}`}>
                            {o.fulfillment || "processing"}
                          </span>
                          {o.payment_status === "paid" && (o.fulfillment || "processing") === "processing" && (
                            shipForm?.orderId === o.id ? (
                              <div className="flex flex-wrap items-center gap-2" data-testid={`ship-form-${o.id.slice(0, 8)}`}>
                                <input
                                  value={shipForm.number}
                                  onChange={(e) => setShipForm({ ...shipForm, number: e.target.value })}
                                  placeholder="Tracking №"
                                  data-testid={`tracking-number-${o.id.slice(0, 8)}`}
                                  className="h-8 w-32 border border-[#0B101E]/25 bg-white px-2.5 text-xs text-[#0B101E] focus:border-gold focus:outline-none"
                                />
                                <select
                                  value={shipForm.carrier}
                                  onChange={(e) => setShipForm({ ...shipForm, carrier: e.target.value })}
                                  data-testid={`carrier-${o.id.slice(0, 8)}`}
                                  className="h-8 border border-[#0B101E]/25 bg-white px-1.5 text-xs text-[#0B101E] focus:border-gold focus:outline-none"
                                >
                                  <option value="dhl">DHL</option>
                                  <option value="ups">UPS</option>
                                  <option value="fedex">FedEx</option>
                                  <option value="usps">USPS</option>
                                  <option value="other">Other</option>
                                </select>
                                <button onClick={() => setOrderStatus(o, "shipped", { tracking_number: shipForm.number || null, carrier: shipForm.carrier })} data-testid={`confirm-ship-${o.id.slice(0, 8)}`} className="bg-[#0B101E] px-3 py-1.5 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-gold">
                                  Ship It
                                </button>
                                <button onClick={() => setShipForm(null)} aria-label="Cancel shipping" data-testid={`cancel-ship-${o.id.slice(0, 8)}`} className="text-[#0B101E]/40 hover:text-burgundy">
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => setShipForm({ orderId: o.id, number: "", carrier: "dhl" })} data-testid={`ship-order-${o.id.slice(0, 8)}`} className="border border-[#0B101E]/20 px-3 py-1.5 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-[#0B101E] transition-colors duration-200 hover:bg-[#0B101E] hover:text-white">
                                Mark Shipped
                              </button>
                            )
                          )}
                          {o.tracking_number && (
                            <p className="mt-1.5 text-[0.6rem] uppercase tracking-[0.14em] text-[#0B101E]/45" data-testid={`tracking-shown-${o.id.slice(0, 8)}`}>
                              {(o.carrier || "").toUpperCase()} {o.tracking_number}
                            </p>
                          )}
                          {o.payment_status === "paid" && o.fulfillment === "shipped" && (
                            <button onClick={() => setOrderStatus(o, "delivered")} data-testid={`deliver-order-${o.id.slice(0, 8)}`} className="border border-[#0B101E]/20 px-3 py-1.5 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-[#0B101E] transition-colors duration-200 hover:bg-gold hover:text-[#0B101E]">
                              Mark Delivered
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="mt-8 border border-navy/10 bg-white/5 p-8" data-testid="golive-panel">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-serif text-2xl text-navy">Go-Live Checklist</p>
                <p className="mt-1.5 text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/50" data-testid="golive-mode">
                  Mode: {golive?.mode === "live" ? "Live — real payments" : "Test — Stripe sandbox"}
                </p>
              </div>
              <button onClick={loadGoLive} data-testid="golive-refresh" className="border border-navy/20 px-5 py-2.5 text-[0.6rem] font-bold uppercase tracking-[0.22em] text-navy transition-colors duration-300 hover:border-gold hover:text-gold">
                Re-check
              </button>
            </div>
            {!golive ? (
              <div className="flex justify-center py-14"><Loader2 className="h-7 w-7 animate-spin text-gold" strokeWidth={1.4} /></div>
            ) : (
              <ul className="mt-8 space-y-4">
                {golive.checks.map((c) => (
                  <li key={c.key} className="flex items-start gap-4 border-b border-navy/10 pb-4 last:border-0" data-testid={`golive-${c.key}`}>
                    {c.ok ? <Check className="mt-0.5 h-5 w-5 shrink-0 text-gold" strokeWidth={2} /> : <X className="mt-0.5 h-5 w-5 shrink-0 text-burgundy" strokeWidth={2} />}
                    <div>
                      <p className="text-sm font-semibold text-navy">{c.label}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-navy/50">{c.hint}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-8 border border-gold/30 bg-gold/5 p-6">
              <p className="font-serif text-xl italic text-navy">Claiming Stripe, step by step</p>
              <ol className="mt-4 list-decimal space-y-2.5 pl-5 text-sm leading-relaxed text-navy/70">
                <li>Claim the test sandbox into your own Stripe account from your Emergent connections page, then complete Stripe's business verification (KYC).</li>
                <li>Tell me once it is claimed — I will swap in your live keys, re-verify a full checkout, and flip the switch.</li>
                <li>Point your custom domain, and Locka steps onto the live runway.</li>
              </ol>
            </div>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-h-[92vh] w-[94vw] max-w-lg overflow-y-auto border border-navy/10 bg-cream sm:rounded-none" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">{editing ? "Refine the piece" : "A new piece joins the maison"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="mt-4 space-y-5" data-testid="admin-product-form">
            <label className="block">
              <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Name</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name" className={INPUT_CLS} />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Category</span>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-form-category" className={`${INPUT_CLS} px-3`}>
                  <option value="classic-vintage">Classic Vintage</option>
                  <option value="modern-luxe">Modern Luxe</option>
                  <option value="limited-edition">Limited Editions</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Price (USD)</span>
                <input required type="number" step="0.01" min="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-form-price" className={INPUT_CLS} />
              </label>
            </div>
            <label className="block">
              <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Description</span>
              <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-form-description" className="w-full border border-navy/20 bg-white/5 p-4 text-navy focus:border-gold focus:outline-none" />
            </label>
            <label className="block">
              <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Image path</span>
              <input required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="/images/product-….jpg" data-testid="product-form-image" className={INPUT_CLS} />
            </label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={generateImage}
                disabled={genLoading}
                data-testid="product-form-generate-image"
                className="flex items-center gap-2 border border-gold/60 px-5 py-3 text-[0.62rem] font-bold uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0B101E] disabled:opacity-50"
              >
                {genLoading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <Sparkles className="h-4 w-4" strokeWidth={1.8} />}
                {genLoading ? "Art-directing…" : "Generate AI Photography"}
              </button>
              <label className={`flex cursor-pointer items-center gap-2 border border-navy/25 px-5 py-3 text-[0.62rem] font-bold uppercase tracking-[0.22em] text-navy transition-colors duration-300 hover:border-gold hover:text-gold ${uploadLoading ? "opacity-50" : ""}`} data-testid="product-form-upload-image">
                {uploadLoading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <Upload className="h-4 w-4" strokeWidth={1.8} />}
                Upload Photo
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} disabled={uploadLoading} />
              </label>
              {form.image && (
                <img src={form.image} alt="Product preview" className="h-14 w-11 border border-navy/15 object-cover" data-testid="product-form-image-preview" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Tag (optional)</span>
                <input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="e.g. 1 of 300" data-testid="product-form-tag" className={INPUT_CLS} />
              </label>
              <label className="block">
                <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Drop closes (limited editions)</span>
                <input type="datetime-local" value={form.drop_ends_at} onChange={(e) => setForm({ ...form, drop_ends_at: e.target.value })} data-testid="product-form-drop" disabled={form.category !== "limited-edition"} className={`${INPUT_CLS} disabled:opacity-40`} />
              </label>
            </div>
            <div>
              <span className="mb-2 block text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/60">Stock per size</span>
              <div className="grid grid-cols-3 gap-4">
                {["S", "M", "L"].map((s) => (
                  <label key={s} className="block">
                    <span className="mb-1.5 block text-[0.58rem] font-bold uppercase tracking-[0.2em] text-gold">Size {s}</span>
                    <input type="number" min="0" value={form.sizes[s]} onChange={(e) => setForm({ ...form, sizes: { ...form.sizes, [s]: e.target.value } })} data-testid={`product-form-stock-${s.toLowerCase()}`} className={INPUT_CLS} />
                  </label>
                ))}
              </div>
            </div>
            <button type="submit" disabled={saving} data-testid="product-form-submit" className="w-full bg-navy py-4 text-[0.7rem] font-bold uppercase tracking-[0.28em] text-cream transition-colors duration-300 hover:bg-burgundy hover:text-white disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save Refinements" : "Add to Collection"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
